import * as React from "react";
import { usePortalSeo } from "../config";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient, ApiError, type EnrolmentMoveInvite } from "@alight/contracts";
import { Button, Card, CardContent, Container, PageHeader, Section } from "@alight/ui";

/**
 * /move/confirm?token=… — the page the confirmation link lands on.
 *
 * A host has asked for a child's registration to be moved under this address.
 * That is a claim about whose child it is, so the address itself decides: the
 * page DESCRIBES the move first and moves nothing until the button is pressed.
 *
 * Two deliberate differences from the sign-in link next door
 * (journey/auth-verify.tsx), which fires the moment it loads:
 *
 *  - Reading the link does not use it up. Someone forwarded this email, or
 *    opened it twice, has not thereby agreed to anything.
 *  - The confirmation is a click, not a page load. A mail scanner following
 *    the URL must not be able to adopt a child on the recipient's behalf.
 */

const api = apiClient("");

export function MoveConfirmPage() {
  usePortalSeo("Confirm a registration");
  const { token } = useSearchToken();

  const invite = useQuery({
    queryKey: ["move-invite", token],
    queryFn: () => api.enrolmentMoveInvite(token),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
  });

  const confirm = useMutation({
    mutationFn: () => api.confirmEnrolmentMove(token),
  });

  const org = invite.data?.org_name ?? null;

  return (
    <>
      <PageHeader
        eyebrow={org ?? "Registration"}
        title={confirm.isSuccess ? "That's moved across" : "Confirm a registration"}
        description={
          confirm.isSuccess
            ? "Thanks — the records are now under your email address."
            : "A registration has been asked to move onto your email address."
        }
      />
      <Section tone="paper" spacing="lg">
        <Container width="narrow">
          <Card>
            <CardContent className="space-y-4 p-6 sm:p-8">
              {!token ? (
                <Message>
                  This link is missing its confirmation code. Please open the link straight from the
                  email, or ask the office to send a fresh one.
                </Message>
              ) : invite.isLoading ? (
                <p className="text-sm text-muted">Looking up this request…</p>
              ) : confirm.isSuccess ? (
                <Done
                  childName={confirm.data.child_name}
                  count={confirm.data.registrations_moved}
                  org={confirm.data.org_name}
                />
              ) : invite.data ? (
                <Invitation
                  invite={invite.data}
                  onConfirm={() => confirm.mutate()}
                  busy={confirm.isPending}
                  error={
                    confirm.error instanceof ApiError
                      ? confirm.error.message
                      : confirm.error
                        ? "Something went wrong — please try again."
                        : null
                  }
                />
              ) : (
                <Message>
                  We couldn&apos;t look up this request. Please try the link from your email again,
                  or contact the office.
                </Message>
              )}
            </CardContent>
          </Card>
        </Container>
      </Section>
    </>
  );
}

function Invitation({
  invite,
  onConfirm,
  busy,
  error,
}: {
  invite: EnrolmentMoveInvite;
  onConfirm: () => void;
  busy: boolean;
  error: string | null;
}) {
  if (invite.state === "confirmed") {
    return <Message>This has already been confirmed — the registration is under your email.</Message>;
  }
  if (invite.state === "cancelled") {
    return <Message>This request was withdrawn by the office, so there is nothing to confirm.</Message>;
  }
  if (invite.state === "expired") {
    return (
      <Message>
        This link has expired. Confirmation links last 7 days — please ask the office to send you a
        fresh one.
      </Message>
    );
  }
  if (invite.state === "unknown") {
    return (
      <Message>
        We don&apos;t recognise this link. Please open it straight from the email, or ask the office
        to send a fresh one.
      </Message>
    );
  }

  const child = invite.child_name ?? "this child";
  const count = invite.registrations || 1;

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-base text-ink">
          <span className="font-semibold">{invite.org_name ?? "The office"}</span> has been asked to
          put{" "}
          <span className="font-semibold">
            {child}
            {count > 1 ? `'s ${count} registrations` : "'s registration"}
          </span>{" "}
          under <span className="font-semibold">{invite.to_email}</span>.
        </p>
        {invite.program_label ? (
          <p className="text-sm text-muted">{invite.program_label}</p>
        ) : null}
      </div>

      <p className="text-sm text-muted">
        Confirming means everything about the program for {child} comes to this address from now on,
        and you can view the registration here without a password. Nothing else about it changes.
      </p>

      {error ? (
        <p role="alert" className="text-sm font-medium text-accent">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={onConfirm} disabled={busy}>
          {busy ? "Confirming…" : `Yes, ${child} is my child`}
        </Button>
      </div>

      <p className="text-xs text-muted">
        If you don&apos;t recognise this, simply close this page — nothing will change, and the link
        stops working on its own.
      </p>
    </div>
  );
}

function Done({
  childName,
  count,
  org,
}: {
  childName?: string | null;
  count: number;
  org?: string | null;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-primary">
        ✓ {childName ?? "The registration"}
        {count > 1 ? `'s ${count} registrations are` : " is"} now under your email address.
      </p>
      <p className="text-sm text-muted">
        We&apos;ve emailed you a copy for your records. Everything about the program from{" "}
        {org ?? "us"} will come to this address from now on.
      </p>
    </div>
  );
}

function Message({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted">{children}</p>;
}

/**
 * The token, read from the query string directly rather than through the
 * router's typed search. This screen is mounted by two apps whose route trees
 * are generated separately, and the value is a raw opaque string either way.
 */
function useSearchToken(): { token: string } {
  const [token] = React.useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("token") ?? "";
  });
  return { token };
}
